import React, { useState } from "react";
import { FaUpload } from "react-icons/fa";
import MedicineScheduleList from "../../components/medicine-schedules/MedicineScheduleList";
import Modal from "../../components/medicine-schedules/Modal";
import ScheduleTaggingForm from "../../components/medicine-schedules/ScheduleTaggingForm";
import Layout from "./Layout";
import { ApiEndPoint } from "../../../_helper/constant/api-types";
import { secureAxios } from "../../../_helper/constant/auth";
import { useAtom } from "jotai";
import { pharmacyAtom } from "../../../_helper/constant/constant-atom";
import { useDeleteItem } from "../../../_helper/actions/mutation/items";
import { toast } from "react-toastify";
import { useItemsFilter } from "../../../_helper/actions/app/items";

const MedicineSchedulePage = ({ pharmacy_id }) => {
  const [pharmacy] = useAtom(pharmacyAtom);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMedicine, setEditingMedicine] = useState(null);
  const [uploadReport, setUploadReport] = useState(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  const deleteMutation = useDeleteItem(ApiEndPoint.PHARMACY);

  const { data: taggedMedinceData = [], refetch } = useItemsFilter(
    ApiEndPoint.PHARMACY,
    `?action=getScheduleTagging&pharmacy_id=${pharmacy?.id}`
  );

  const handleAddMedicine = (newMedicine) => {
    // API call to add the new medicine can be placed here.
    setIsModalOpen(false);
  };

  // Edit callback: set the medicine to be edited and open the modal.
  const handleEditMedicine = (medicine) => {
    setEditingMedicine(medicine);
    setIsModalOpen(true);
  };

  // Delete callback: trigger an API call to delete the selected medicine.
  const handleDeleteMedicine = async (id) => {
    deleteMutation.mutate(
      { data: { id, action: "delScheduleTagging" } },
      {
        onSuccess: () => {
          toast.success("Medicine Removed from the list.");
          refetch();
        },
        onError: () => {
          toast.error("There was an issue removing the medicine.");
        },
      }
    );
  };

  const handleBulkUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("action", "BulkCreateScheduleTagging");
      formData.append("pharmacy_id", pharmacy?.id);

      try {
        const response = await secureAxios.post(
          ApiEndPoint.PHARMACY,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        refetch();
        toast.success(`${response.message}`);
        // Check if the API response includes a report of skipped medicines
      } catch (error) {
        toast.error("Error uploading file: " + error.message);
      }
    }
  };

  return (
    <Layout>
      <div className="p-6 bg-gray-100 min-h-screen">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-gray-800">
              Medicine Schedule Management
            </h1>
            <div className="flex gap-4">
              <button
                onClick={() => {
                  setEditingMedicine(null);
                  setIsModalOpen(true);
                }}
                className="bg-blue-600 text-white py-2 px-6 rounded-lg shadow hover:bg-blue-700 transition-all"
              >
                + Add Medicine
              </button>
              <label
                htmlFor="bulk-upload"
                className="bg-green-600 text-white py-2 px-4 rounded-lg shadow flex items-center gap-2 cursor-pointer hover:bg-green-700 transition-all"
              >
                <FaUpload size={16} />
                Bulk Upload Medicines
                <input
                  type="file"
                  id="bulk-upload"
                  className="hidden"
                  accept=".csv"
                  onChange={handleBulkUpload}
                />
              </label>
            </div>
          </div>

          {/* Description */}
          <p className="text-gray-600 mt-4">
            The Medicine Schedule Management module allows administrators to
            oversee and manage medicines effectively. It provides
            functionalities to:
          </p>
          <ul className="list-disc list-inside text-gray-600 mt-2 space-y-1">
            <li>
              Maintain an up-to-date list of medicines along with their
              schedules.
            </li>
            <li>
              Record and track key attributes like dosage form, strength, and
              prescription requirements.
            </li>
            <li>
              Ensure compliance with regulatory guidelines for each schedule
              type.
            </li>
            <li>
              Add, edit, or update medicine details efficiently through a
              user-friendly interface.
            </li>
          </ul>
        </div>

        {/* Medicine List */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            Medicine List
          </h2>
          <MedicineScheduleList
            onEdit={handleEditMedicine}
            onDelete={handleDeleteMedicine}
            taggedMedinceData={taggedMedinceData}
          />
        </div>

        {/* Modal for Add/Edit */}
        {isModalOpen && (
          <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              {editingMedicine ? "Edit Medicine" : "Add New Medicine"}
            </h2>
            <ScheduleTaggingForm
              pharmacy_id={pharmacy_id}
              medicine={editingMedicine}
              // onSubmit={
              //   editingMedicine ? handleEditMedicine : handleAddMedicine
              // }
              setIsModalOpen={setIsModalOpen}
              isModalOpen={isModalOpen}
            />
          </Modal>
        )}

        {/* Modal for Upload Report */}
        {isReportModalOpen && (
          <Modal
            isOpen={isReportModalOpen}
            onClose={() => setIsReportModalOpen(false)}
          >
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Bulk Upload Report
            </h2>
            <div className="max-h-96 overflow-y-auto">
              {uploadReport && uploadReport.length > 0 ? (
                <table className="min-w-full border-collapse">
                  <thead className="bg-blue-600 text-white">
                    <tr>
                      <th className="py-2 px-3 text-left text-sm font-semibold uppercase">
                        Medicine
                      </th>
                      <th className="py-2 px-3 text-left text-sm font-semibold uppercase">
                        Reason
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadReport.map((item, index) => (
                      <tr
                        key={index}
                        className={index % 2 === 0 ? "bg-gray-100" : "bg-white"}
                      >
                        <td className="py-2 px-3 text-sm text-gray-700">
                          {item.medicine}
                        </td>
                        <td className="py-2 px-3 text-sm text-gray-700">
                          {item.reason}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-gray-700">
                  All medicines were processed successfully.
                </p>
              )}
            </div>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setIsReportModalOpen(false)}
                className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </Modal>
        )}
      </div>
    </Layout>
  );
};

export default MedicineSchedulePage;
